import { Component, Input, OnInit } from '@angular/core';
import { ModuleManagerCheckMenu } from 'src/shared/models/module-manager/module-manager-menu-item.model';
import { ModuleManagerExportFormat, ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';
import { ModuleManager } from 'src/shared/models/module-manager'
import { KpiTrendModuleData, KPIWidgetDisplayItems } from './extended-kpi.model';
import { ErrorCode } from 'src/shared/models/error-code.model';
import { ExtendedKpiService } from './extended-kpi.service';
import { ApiResponse } from 'src/AngularJS/Models/api-response.model';

@Component({
	selector: 'cv-extended-kpi',
	templateUrl: './extended-kpi.component.html',
	styleUrls: ['./extended-kpi.component.scss']
})
export class ExtendedKpiComponent implements OnInit {
    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;

    public isDataLoading = false;  // This is a bookkeeping variable used to prevent multiple outgoing requests for data.
    public isResizing = false;
    public isFirstLoad = true; // This variable is used for logging.  ModuleResponsiveController should accept isAutoRefresh and isFirstLoad variables.

    public data: KpiTrendModuleData = null; // A copy of data returned by loadData will be stored here.

    private stateSaveTimer: number;
    private resizeTimer: number;

    // For objective bar
    public hover: boolean[];

    // For dynamic sizing of the module
    private numRows: number;
    private numColumns: number;
    private itemWidth: number;
    private itemHeight: number;

    public displayItems: KPIWidgetDisplayItems = {
        Labels: true,
        Changes: true,
        Histogram: true,
        Objectives: true,
    }

    private displayItemsList = [
        {
            displayName: "Labels",
            checked: () => this.displayItems.Labels
        },
        {
            displayName: "Changes",
            checked: () => this.displayItems.Changes
        },
        {
            displayName: "Histogram",
            checked: () => this.displayItems.Histogram
        },
        {
            displayName: "Objectives",
            checked: () => this.displayItems.Objectives
        },
    ];

	constructor(
        private ExtendedKpiService: ExtendedKpiService,
	) { }

	ngOnInit(): void {
		this.moduleManager.showLoader = true;

        // Module Configuration:
        this.moduleManager.setOptions({
            enableWidthClass: false,
            widgetMode: false,
            onSettingsApplied: settings => this.loadData(false),
            onExport: this.onExport.bind(this),
            hasRefresh: true,
            refreshType: ModuleManagerRefreshType.Auto,
            onRefresh: isAutoRefresh => { return this.loadData(isAutoRefresh); },
            canRefresh: () => !this.isDataLoading,
        });

        this.moduleManager.addState({
            name: "displayItems",
            getStateData: () => {
                return this.displayItems;
            },
            onStateLoaded: (stateData) => {
                if (typeof stateData !== 'object' || !('Labels' in stateData)) {
                    this.displayItems = {
                        Labels: true,
                        Changes: true,
                        Histogram: true,
                        Objectives: true
                    }
                    return;
                }

                this.displayItems = stateData;
                this.updateItemsSize();
                this.updateItems();
            },
            autoLoad: true
        });

		this.moduleManager.addMenuItem({
            name: "Display",
            icon: "legend",
            type: "checkMenu",
            getEnabled: () => !!this.data && this.data.KpiData.length > 0,
            getCheckItems: () => this.displayItemsList,
            getItemName: (i) => this.displayItemsList[i].displayName,
            isItemChecked: (i) => this.displayItemsList[i].checked(),
            onCheckClicked: (i) => this.displayChanged(i)
        } as ModuleManagerCheckMenu);

        this.moduleManager.registerEvents({
            'onResize': () => {
                this.updateItems()
            }
        });
        
        this.updateItemsSize();
	}

	/**
     * Saves the grid's state save, debounced to 500ms.
     */
	 private queueStateSave(): void {
        // Cancel previous save attempt, since it hasn't been 500ms, yet.
        if (this.stateSaveTimer) {
            clearTimeout(this.stateSaveTimer);
            this.stateSaveTimer = null;
        }

        // Create a timer to save the state 500ms from now.
       this.stateSaveTimer = window.setTimeout(() => {
            this.stateSaveTimer = null;
            this.moduleManager.saveState(); // This will save all moduleManager.addState definitions
        }, 500);
    }

	/**  */
    private async loadData(isAutoRefresh: boolean): Promise<void> {
        if (this.isDataLoading)
            return; // don't have multiple outgoing requests
        this.isDataLoading = true;

        if (!isAutoRefresh)
            this.moduleManager.showLoader = true;

        try {
            this.data = await this.ExtendedKpiService.loadKPIData(this.moduleKey, isAutoRefresh, this.isFirstLoad);
            this.isFirstLoad = false;
            this.moduleManager.container.showDateRange = true;

            this.hover = this.data.KpiData.map(d => false);

            
            this.updateItems();
            this.moduleManager.setAutoRefreshReady();
        } catch (err) {
            this.moduleManager.handleLoadError(err, isAutoRefresh);
        } finally {
            this.isDataLoading = false;
            this.moduleManager.showLoader = false;
        }
    }

	private displayChanged(i: number): void {
        this.displayItems[this.displayItemsList[i].displayName] = !this.displayItems[this.displayItemsList[i].displayName];
        this.queueStateSave();
        this.updateItemsSize();
        this.updateItems();
    }

    /**
     * Update minimum width and height of each items based on which component is shown
     */
    private updateItemsSize(): void {
        this.itemWidth = this.displayItems.Histogram ? 300 : 250;
        this.itemHeight = 160;
        
        // If changes are hidden, remove 35 pixels from min height
        if (!this.displayItems.Changes)
            this.itemHeight -= 35;
        // If only change labels are hidden, remove 15 pixels from min height instead of 35 pixels
        else if (!this.displayItems.Labels)
            this.itemHeight -= 15;
        // If objectives are hidden, remove 20 pixels from min height
        if (!this.displayItems.Objectives)
            this.itemHeight -= 20;
    }

	private updateItems(): void {
        var container = document.getElementById(`${this.moduleKey}_kpiWrapper`);
        
        if (this.data?.Error == ErrorCode.NoError && this.data?.KpiData && container?.clientHeight > 0) {
            this.numRows = Math.floor(container.clientHeight / this.itemHeight);
            this.numColumns = Math.ceil(this.data.KpiData.length / this.numRows);
            
            this.moduleManager.showLoader = false;
            return;
        }
        
        if (this.resizeTimer) {
            clearTimeout(this.resizeTimer);
            this.resizeTimer = null;
        }
        
        this.resizeTimer = window.setTimeout(() => {
            this.updateItems();
            
            this.resizeTimer = null;
        }, 500);
    }

	public getContainerSize(): Object {
        return {
            'width': `${this.itemWidth * this.numColumns}px`,
            'height': `${this.itemHeight * this.numRows}px`
        };
    }
    
    public getItemSize(): Object {
        return {
            'width': `${this.itemWidth}px`,
            'height': `${this.itemHeight}px`
        };
    }

	public getSeparators(index: number): string {
        var str = "";
        
        if (Math.floor(index / this.numRows) != 0)
            str = "vertical-separator";

        if (index % this.numRows != 0)
            str += " horizontal-separator";
            
        return str;
    }

    public mouseover(index: number): void {
        if (this.data.KpiData[index].BarData.segments.length < 2) return;

        this.hover[index] = true;
    }

    public mouseleave(index: number): void {
        if (this.data.KpiData[index].BarData.segments.length < 2) return;

        this.hover[index] = false;
    }

	/**
     * Get formated data ready to export
     * @param format
     */
	public async onExport(format: ModuleManagerExportFormat): Promise<ApiResponse<string>> {
        return await this.ExtendedKpiService.getDataForExport(this.moduleKey, format);
    }
}
